/**
 * sheet_form_controller.js — closes the standalone form page a native sheet hosts.
 *
 * Mounted on the .native-form-page wrapper in templates/native/form_page.html,
 * which is the ONLY page this controller is for. Identifier "sheet-form" — not
 * to be confused with esm/sheet_controller.js ("sheet", the web drawer over
 * Bootstrap's modal) or esm/bridge/sheet_controller.js ("bridge--sheet", the
 * seam that hands a URL to the native presenter).
 *
 * WHY THIS EXISTS
 * A native sheet is a second WKWebView presented over the main one
 * (native/ios/Chippie/Bridge/SheetComponent.swift). It has no navbar and no
 * close button of its own, so the ONLY thing that can retire it is the native
 * host. The host's signal is a real main-frame navigation away from the path the
 * sheet was opened on — SheetWebViewController.decidePolicyFor cancels that
 * navigation and dismisses instead.
 *
 * Nothing on these pages was producing one. Every form here submits through
 * Turbo, and a Turbo submit is a fetch: the success path is answered with
 * text/vnd.turbo-stream.html (clients.add_quote, clients.update_client and
 * friends all return streams whenever can_stream(), which a Turbo submit always
 * makes true), the streams target ids that live on the list and detail pages and
 * so match nothing here, and the WKWebView never navigates. Even a 302 would not
 * help: Turbo follows a redirect with fetch + history.pushState, and neither is
 * a navigation the native delegate sees.
 *
 * So the sheet stayed up forever. Reported from the field on 2026-09-04: a quote
 * was created, the success toast fired, quote_form.js reset the form under the
 * user — who read the blank fields as "it didn't save" — and the sheet would not
 * go away. The only exit was force-quitting the app. Same for Edit Client, where
 * the row updated in the database on every one of four taps while the button
 * looked dead.
 *
 * WHAT IT DOES
 * Performs the navigation the native host is waiting for, with
 * window.location.assign — deliberately NOT Turbo.visit, which would be another
 * fetch and would be invisible to the delegate all over again. On native the
 * host cancels it and dismisses the sheet; the main web view underneath is
 * already live-updating from the Mercure broadcast every mutating route fires
 * (TurboResponseBuilder._broadcast_entity_change), so it shows the change with
 * no reload. In a plain browser nothing cancels it and the page simply lands on
 * doneUrl, which is why the value is a real destination and not a sentinel.
 *
 * doneUrl comes from url_for in application/web/views/native.py, so route shapes
 * keep one owner. It MUST differ from the form page's own path or the native
 * check (`target.path != formPath`) will not fire.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = { doneUrl: String };

    connect() {
        // Set once we have committed to leaving. A second submit-end or a second
        // tap during the dismissal animation must not assign location twice.
        this.leaving = false;

        this._onSubmitEnd = this.submitEnd.bind(this);
        this._onClick = this.click.bind(this);

        // turbo:submit-end is dispatched on the <form> and bubbles, so listening
        // on the wrapper covers both shapes this page renders: the form it owns
        // itself (the edit partials) and the one a create partial brings with it.
        this.element.addEventListener("turbo:submit-end", this._onSubmitEnd);
        this.element.addEventListener("click", this._onClick);
    }

    disconnect() {
        this.element.removeEventListener("turbo:submit-end", this._onSubmitEnd);
        this.element.removeEventListener("click", this._onClick);
    }

    submitEnd(event) {
        // A failed submit re-renders errors in place and the sheet must stay up
        // for the user to fix them. Only success retires it.
        if (!event.detail || !event.detail.success) return;
        this.leave();
    }

    /** The page's own close control. */
    done(event) {
        if (event) event.preventDefault();
        this.leave();
    }

    /**
     * Adopt the create partials' Cancel button.
     *
     * components/forms/{quote,client}_form.html are shared with the dashboard and
     * the list pages, where Cancel means "collapse the inline form panel" and is
     * wired to hideAllForms via the call-fn delegation. That function is
     * installed by forms_controller, which mounts on #formsContainer — an element
     * only dashboard.html, clients.html and quotes.html have. On this page it is
     * simply undefined, so the button did nothing at all, which is the "cancel
     * button wouldn't work" half of the field report.
     *
     * Intercepting the click here rather than editing the partial keeps the
     * shared template untouched and puts the adaptation where the other
     * sheet-only adaptations already live. The guard is on hideAllForms being
     * absent, so if this page ever does mount forms_controller the real handler
     * still wins.
     */
    click(event) {
        const cancel = event.target.closest && event.target.closest('[data-fn="hideAllForms"]');
        if (!cancel) return;
        if (typeof window.hideAllForms === "function") return;
        event.preventDefault();
        this.leave();
    }

    leave() {
        if (this.leaving) return;
        const url = this.doneUrlValue;
        // No destination means we would navigate to the current path, which the
        // native host reads as "still on the form" and ignores. Staying put is
        // wrong but honest; silently going somewhere invented is worse.
        if (!url) return;
        this.leaving = true;
        window.location.assign(url);
    }
}
